
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Tables } from "@/integrations/supabase/types";

type ActivityCategory = Tables<"activity_categories">;
type ActivityCategoryInsert = {
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  display_order?: number;
  status?: "draft" | "published" | "archived";
};

export const useActivityCategories = () => {
  const [categories, setCategories] = useState<ActivityCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("activity_categories")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to fetch activity categories: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveCategory = async (categoryData: ActivityCategoryInsert, editingCategoryId?: string) => {
    try {
      if (editingCategoryId) {
        const { error } = await supabase
          .from("activity_categories")
          .update(categoryData)
          .eq("id", editingCategoryId);

        if (error) throw error;
        toast({ title: "Success", description: "Category updated successfully!" });
      } else {
        const { error } = await supabase
          .from("activity_categories")
          .insert(categoryData);

        if (error) throw error;
        toast({ title: "Success", description: "Category created successfully!" });
      }

      fetchCategories();
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteCategory = async (categoryId: string) => {
    if (!confirm("Are you sure you want to delete this category? This action cannot be undone.")) {
      return false;
    }

    try {
      const { error } = await supabase
        .from("activity_categories")
        .delete()
        .eq("id", categoryId);

      if (error) throw error;

      toast({ title: "Success", description: "Category deleted successfully!" });
      fetchCategories();
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to delete category: " + error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return {
    categories,
    loading,
    fetchCategories,
    saveCategory,
    deleteCategory,
  };
};
